(() => {
  if (customElements.get('aaronya-collection-colorways')) return;

  const productCache = new Map();

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    }).format((Number(cents) || 0) / 100);
  };

  const productPath = (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    return root + 'products/' + encodeURIComponent(handle);
  };

  const loadProduct = async (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(productPath(handle) + '.js', {
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error('Unable to load colorway');
        return response.json();
      }).catch((error) => {
        productCache.delete(handle);
        throw error;
      }));
    }
    return productCache.get(handle);
  };

  class AaronyaCollectionColorways extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('focusin', this.onPointerOver);
    }

    onPointerOver = (event) => {
      const swatch = event.target.closest('[data-aaronya-colorway]');
      if (swatch && swatch.dataset.handle) loadProduct(swatch.dataset.handle);
    };

    onClick = async (event) => {
      const more = event.target.closest('[data-aaronya-colorway-more]');
      if (more) {
        this.querySelectorAll('.is-extra[hidden]').forEach((swatch) => {
          swatch.hidden = false;
        });
        more.setAttribute('aria-expanded', 'true');
        more.hidden = true;
        return;
      }

      const swatch = event.target.closest('[data-aaronya-colorway]');
      if (!swatch || swatch.classList.contains('is-selected')) return;

      event.preventDefault();
      event.stopPropagation();
      swatch.classList.add('is-loading');

      try {
        const product = await loadProduct(swatch.dataset.handle);
        this.updateCard(product, swatch);
      } catch (error) {
        const status = this.querySelector('[data-aaronya-colorway-status]');
        if (status) status.textContent = 'That color could not be loaded. Please try again.';
      } finally {
        swatch.classList.remove('is-loading');
      }
    };

    updateCard(product, selectedSwatch) {
      const card = this.closest('product-card');
      if (!card) return;

      const url = product.url || productPath(selectedSwatch.dataset.handle);
      const image = product.featured_image || product.images?.[0];
      const title = product.title || selectedSwatch.dataset.color;
      const availableVariants = product.variants?.filter((variant) => variant.available) || [];
      const pricedVariants = availableVariants.length ? availableVariants : (product.variants || []);
      const lowestVariant = pricedVariants.reduce((lowest, variant) => {
        if (!lowest) return variant;
        return Number(variant.price || 0) < Number(lowest.price || 0) ? variant : lowest;
      }, null);

      card.dataset.productId = String(product.id || '');
      card.dataset.selectedHandle = selectedSwatch.dataset.handle;
      card.setAttribute('data-featured-media-url', image || '');

      const mainLink = card.querySelector('[ref="productCardLink"]');
      if (mainLink) {
        mainLink.setAttribute('href', url);
        mainLink.setAttribute('aria-label', title);
      }

      const titleLink = card.querySelector('[ref="productTitleLink"]');
      if (titleLink) {
        titleLink.setAttribute('href', url);
        const heading = titleLink.querySelector('[role="heading"]');
        if (heading) heading.textContent = title;
      }

      card.querySelectorAll('[ref="cardGalleryLink"]').forEach((link) => {
        link.setAttribute('href', url);
        link.setAttribute('aria-label', title);
      });

      const zoomTitle = card.querySelector('.product-grid-view-zoom-out--details h3');
      if (zoomTitle) zoomTitle.textContent = title;

      if (image) {
        card.querySelectorAll('.card-gallery img').forEach((img) => {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = image;
          img.alt = title;
        });
        card.querySelectorAll('.card-gallery source').forEach((source) => {
          source.srcset = image;
        });
      }

      const priceContainer = card.querySelector('product-price [ref="priceContainer"]');
      if (priceContainer && lowestVariant) {
        const price = money(lowestVariant.price);
        const template = this.dataset.priceFromTemplate || 'From __PRICE__';
        const priceElement = document.createElement('span');
        priceElement.className = 'price';
        priceElement.textContent = template.replace('__PRICE__', price);
        priceContainer.replaceChildren(priceElement);
      }

      const quickAdd = card.querySelector('quick-add-component');
      if (quickAdd) {
        quickAdd.dataset.productId = String(product.id || '');
        quickAdd.dataset.productTitle = title;
        if (typeof quickAdd.fetchProductPage === 'function') quickAdd.fetchProductPage(url);
      }

      this.querySelectorAll('[data-aaronya-colorway]').forEach((button) => {
        const selected = button === selectedSwatch;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.dataset.currentHandle = selectedSwatch.dataset.handle;

      const status = this.querySelector('[data-aaronya-colorway-status]');
      if (status) status.textContent = title + ' selected';
    }
  }

  customElements.define('aaronya-collection-colorways', AaronyaCollectionColorways);
})();
